import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { ChannelSelect, DateDurationSelect, GuestsSelect, RegionSelect, SearchBoxMobile, useHandleSearch } from '.';
import { config } from '../../../config';
import t from '../../../text';
import { usePageWidth } from '../../hooks/usePageWidth';
import { minPricesMonthsSelector } from '../../redux/data/cachedAvail/selectors';
import { selectMonth } from '../../redux/search/form/actions';
import {
  getIsAdultsInvalidSelector,
  isInvalidNightsSelector,
  searchFormArrivalDateSelector,
  searchFormMonthSelector,
  searchFormParamsSelector,
} from '../../redux/search/form/selectors';
import { useNewsletter } from '../Footer/NewsletterContext';
import { EmailSignUp } from './EmailSignUp';
import classNames from 'classnames';

export const SearchBox = ({ includeEmail = false, isHomePage }) => {
  const dispatch = useDispatch();
  const { width } = usePageWidth();
  const className = isHomePage ? '' : 'bg-dark pt-3 pb-3 pb-lg-2';

  const month = useSelector(searchFormMonthSelector);
  const arrivalDate = useSelector(searchFormArrivalDateSelector);

  const minPricesMonths = useSelector(minPricesMonthsSelector);

  const [showSearchModal, setShowSearchModal] = useState(false);
  const toggleSearchModal = () => setShowSearchModal((show) => !show);

  useEffect(() => {
    if (!month && !arrivalDate) {
      selectMonth(dispatch, minPricesMonths?.[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On a mobile the search box opens in a modal, now that we have resized to medium we want to close it
  useEffect(() => {
    if (width < 768) return;
    if (showSearchModal) setShowSearchModal(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const { search } = useLocation();
  const isResultsPage = search.length > 0;

  return (
    <Container fluid className={`${className}${isResultsPage ? ' search-box-sticky' : ''}`}>
      {/* Medium */}
      <Container className="search-box d-none d-md-block" id="search-modal">
        <BaseSearchForm setShowSearchModal={setShowSearchModal} includeEmail={includeEmail} />
      </Container>

      {/* Mobile UI */}
      <SearchBoxMobile toggleSearchModal={toggleSearchModal} />

      {/* Mobile Modal */}
      <Modal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        centered
        dialogClassName={showSearchModal ? 'is-open' : ''}
        id="search-modal"
      >
        <i className="icon-close h2 close-search-modal" onClick={toggleSearchModal} />
        <Modal.Body className="pt-0 pb-4">
          <BaseSearchForm setShowSearchModal={setShowSearchModal} includeEmail={includeEmail} isVertical />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

SearchBox.propTypes = {
  includeEmail: PropTypes.bool,
  isHomePage: PropTypes.bool,
};

const REGULAR_PADDING = 'px-lg-1 px-xl-2';
const VERTICAL_PADDING = 'px-0 px-sm-3';

export const BaseSearchForm = ({ setShowSearchModal, includeEmail, isVertical, textColor = 'white' }) => {
  const params = useSelector(searchFormParamsSelector);
  const isAdultsInvalid = useSelector(getIsAdultsInvalidSelector);
  const invalidNightsSelected = useSelector(isInvalidNightsSelector);
  const disabled = !params.arrivalDate || invalidNightsSelected || isAdultsInvalid;

  const { handleSearch } = useHandleSearch();

  const [email, setEmail] = useState('');

  const { hasSignedUpForDeals } = useNewsletter();

  const showSignUpUI = includeEmail && !hasSignedUpForDeals;

  const formSubmit = (event) => {
    handleSearch(event, email, 'Search box');
    setShowSearchModal?.(false);
  };

  const colProps = isVertical ? { xs: 12 } : { md: 6, lg: true };
  const buttonColProps = isVertical ? { xs: 12 } : { md: 12, lg: 2 };
  const colPadding = isVertical ? VERTICAL_PADDING : REGULAR_PADDING;
  const buttonClassName = classNames(colPadding, 'mt-4', {
    'pl-lg-3 mt-lg-auto mb-lg-3': !isVertical,
  });

  const formFields = useMemo(() => {
    const fields = [
      { key: 'channel', component: <ChannelSelect textStyle={textColor} /> },
      { key: 'region', component: <RegionSelect textStyle={textColor} /> },
      { key: 'date', component: <DateDurationSelect textStyle={textColor} /> },
      { key: 'guests', component: <GuestsSelect textStyle={textColor} /> },
    ];

    if (showSignUpUI) {
      fields.push({ key: 'signUp', component: <EmailSignUp email={email} setEmail={setEmail} /> });
    }

    return fields;
  }, [email, showSignUpUI, textColor]);

  return (
    <Form onSubmit={formSubmit}>
      <Row noGutters={isVertical}>
        {formFields.map(({ key, component }) => (
          <Col key={key} {...colProps} className={colPadding}>
            {component}
          </Col>
        ))}
        <Col {...buttonColProps} className={buttonClassName}>
          <Button block variant={config['searchBox.buttonColour']} type="submit" disabled={disabled}>
            {t('search.search')}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

BaseSearchForm.propTypes = {
  setShowSearchModal: PropTypes.func,
  includeEmail: PropTypes.bool,
  isVertical: PropTypes.bool,
  textColor: PropTypes.string,
};
